import React from "react";
import { fetchQuery, graphql } from "react-relay";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce } from "lodash";

import { environment } from "~/models";
import Avatar from "~/ui/legacy/Avatar";
import AsyncSelector, {
	type AsyncSelectorProps,
	components,
	type GroupBase,
	type GroupHeadingProps,
	type OptionProps,
	type PlaceholderProps,
	type SelectComponentsConfig,
	type SelectorOption,
	type SingleValueProps,
	type SingleValue as SingleValueType,
	type ValueContainerProps,
} from "~/ui/selectors/AsyncSelector";

import type { UserSelectorQuery } from "~/__generated__/UserSelectorQuery.graphql";

import selectorStyles from "~/ui/selectors/Selector.module.css";

import styles from "./UserSelector.module.css";

const INCLUDED_OPTIONS_GROUP_LABEL = "INCLUDED_OPTIONS_GROUP_LABEL";

export interface UserSelectorValue {
	id: string;
	fullName: string;
	isGuest: boolean;
	image: string;
	email?: string;
	isTopUser?: boolean;
	internalAttributes?: {
		manager?: {
			id: string;
			fullName: string;
		} | null;
	} | null;
}

export interface UserSelectorOption extends SelectorOption {
	readonly avatar?: UserSelectorValue;
	readonly email?: string;
}

function getValueContainer(condensed: boolean) {
	return function ValueContainer({ children, ...rest }: ValueContainerProps<UserSelectorOption, false>) {
		const { getValue } = rest;
		const value = getValue()[0];
		return (
			<components.ValueContainer
				{...rest}
				className={value ? cx(styles.valueContainer, { [styles.condensedContainer]: condensed }) : undefined}
			>
				{children}
			</components.ValueContainer>
		);
	};
}

export interface QueryOptions {
	excludeUserIDs?: string[];
	idIn?: string[];
	excludeGuests?: boolean;
	excludeManagedUsers?: boolean;
	excludeNonManagers?: boolean;
	excludeAncestryFor?: string;
	doesNotReportUnder?: string;
	ranked?: boolean;
}

interface GroupedOptionsType {
	label: string;
	options: UserSelectorOption[];
}

// currently only applies to the use in meeting guidelines analytics, where we want to show the
// "All meetings in your workspace" option without a group label, and only want to show the label
// to the group of all the users, to indicate the choice of viewing analytics by organizer, or for org wide.
function formatGroupLabels(group: GroupBase<UserSelectorOption>) {
	if (group.label === INCLUDED_OPTIONS_GROUP_LABEL) {
		return null;
	}
	return <>{group.label}</>;
}

// we need to provide a group heading here to customize the styles properly. Without adding this,
// there is excess vertical padding on the group headings even when no label is provided, leaving
// large spaces visible between groups.
const GroupHeading = ({ children, ...rest }: GroupHeadingProps<UserSelectorOption, false>) => {
	const group = rest.data;
	if (group.label === INCLUDED_OPTIONS_GROUP_LABEL) return null;
	return (
		<components.GroupHeading className={styles.groupHeading} {...rest}>
			{children}
		</components.GroupHeading>
	);
};

function loadOptionsInternal(
	search: string,
	callback: (values: GroupedOptionsType[] | UserSelectorOption[]) => void,
	options: QueryOptions,
	includedOptions: UserSelectorOption[] = [],
	groupLabel: string | undefined = undefined,
) {
	const {
		excludeUserIDs,
		idIn,
		excludeGuests,
		excludeManagedUsers,
		excludeNonManagers,
		excludeAncestryFor,
		doesNotReportUnder,
		ranked,
	} = options;

	fetchQuery<UserSelectorQuery>(
		environment,
		graphql`
			query UserSelectorQuery(
				$search: String
				$isGuests: Boolean
				$isManager: Boolean
				$ranked: Boolean
				$exclude: [ID]
				$doesNotReportUnder: ID
				$excludeManagedUsers: Boolean
				$excludeAncestryFor: ID
				$idIn: [ID]
			) {
				viewer {
					selectUsers: users(
						first: 25
						search: $search
						isGuests: $isGuests
						isManager: $isManager
						ranked: $ranked
						exclude: $exclude
						excludeManagedUsers: $excludeManagedUsers
						excludeAncestryFor: $excludeAncestryFor
						doesNotReportUnder: $doesNotReportUnder
						idIn: $idIn
					) {
						edges {
							node {
								id
								email
								fullName
								isGuest
								image
								internalAttributes {
									manager {
										id
										fullName
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			search,
			isGuests: excludeGuests ? false : undefined,
			isManager: excludeNonManagers ? true : undefined,
			ranked,
			exclude: excludeUserIDs?.filter((id): id is string => Boolean(id)),
			excludeManagedUsers,
			excludeAncestryFor: excludeAncestryFor,
			doesNotReportUnder: doesNotReportUnder,
			idIn: idIn?.filter((id): id is string => Boolean(id)),
		},
		{ fetchPolicy: "store-or-network" },
	)
		.toPromise()
		.then(data => {
			if (!data) return;

			const { viewer } = data;

			const options =
				viewer.selectUsers?.edges.map(user => {
					return {
						value: user.node.id,
						label: user.node.fullName,
						email: user.node.email,
						avatar: {
							id: user.node.id,
							fullName: user.node.fullName,
							isGuest: user.node.isGuest,
							image: user.node.image,
							email: user.node.email,
							internalAttributes: {
								manager: user.node.internalAttributes?.manager ?? null,
							},
						},
					};
				}) ?? [];

			// When searching, filter included options to only show matches
			// When not searching, show all included options
			const filteredIncludedOptions = search
				? includedOptions.filter(
						opt =>
							opt.label.toLowerCase().includes(search.toLowerCase()) ||
							opt.email?.toLowerCase().includes(search.toLowerCase()),
					)
				: includedOptions;

			// Deduplicate options between included and returned options
			const includedIds = new Set(filteredIncludedOptions.map(opt => opt.value));
			const filteredSearchOptions = options.filter(opt => !includedIds.has(opt.value));
			const allOptions = [...filteredIncludedOptions, ...filteredSearchOptions];

			// If the includedOptions are present, split the options into the groups,
			// and the INCLUDED_OPTIONS_LABEL won't be shown (see formatGroupLabels)
			if (includedOptions && groupLabel) {
				callback([
					{ label: INCLUDED_OPTIONS_GROUP_LABEL, options: filteredIncludedOptions },
					{ label: groupLabel, options: filteredSearchOptions },
				]);
			} else {
				callback(allOptions);
			}
		})
		.catch(err => {
			if (err) callback([]);
		});
}

export const loadOptions =
	(options: QueryOptions, includedOptions: UserSelectorOption[] = [], groupLabel: string | undefined = undefined) =>
	(search: string, callback: (values: GroupedOptionsType[] | UserSelectorOption[]) => void) => {
		return loadOptionsInternal(search, callback, options, includedOptions, groupLabel);
	};

interface UserSelectorProps extends QueryOptions, AsyncSelectorProps<UserSelectorOption> {
	isClearable?: boolean;
	placeholder?: React.ReactNode;
	placeholderIcon?: IconDefinition;
	selectedUser?: SingleValueType<UserSelectorOption>;
	onChange?: (newUser: SingleValueType<UserSelectorOption>) => void;
	includedOptions?: UserSelectorOption[];
	groupLabel?: string;
}

export default function UserSelector({
	isClearable = true,
	excludeUserIDs,
	excludeGuests,
	excludeManagedUsers,
	excludeNonManagers,
	doesNotReportUnder,
	placeholderIcon,
	ranked,
	selectedUser,
	groupLabel,
	includedOptions = [],
	...rest
}: UserSelectorProps) {
	const customComponents: SelectComponentsConfig<UserSelectorOption, false, GroupBase<UserSelectorOption>> | undefined =
		{};

	const condensed = rest.condensed;

	customComponents.Option = React.useCallback(
		({ children, data, innerProps, ...rest }: OptionProps<UserSelectorOption, false>) => {
			const { isSelected } = rest;

			// @ts-ignore - Adding 'data-test-id' to the global HTML attribute would fix this but this works for now
			innerProps["data-test-id"] = data["data-test-id"];
			const avatar = data["avatar"];

			return (
				<components.Option {...rest} data={data} innerProps={innerProps}>
					{avatar && <Avatar className={styles.avatarOption} user={avatar} width={condensed ? "16px" : "20px"} />}
					<span className={cx(selectorStyles.optionText, { [styles.condensedOption]: !isSelected && condensed })}>
						{children}
					</span>
					{isSelected && (
						<FontAwesomeIcon
							icon={faCheck}
							className={cx(selectorStyles.optionChecked, { [styles.condensedCheck]: condensed })}
						/>
					)}
				</components.Option>
			);
		},
		[condensed],
	);

	customComponents.SingleValue = React.useCallback(
		({ children, ...rest }: SingleValueProps<UserSelectorOption, false>) => {
			const { getValue } = rest;
			const value = getValue()[0];

			let avatar;

			if (value) {
				if ("avatar" in value) avatar = value["avatar"];
			}

			return (
				<components.SingleValue {...rest} className={cx(styles.singleValue, { [styles.condensed]: condensed })}>
					{avatar && <Avatar className={styles.singleValueAvatar} user={avatar} width={condensed ? "14px" : "24px"} />}
					<span className={styles.singleValueText}>{children}</span>
				</components.SingleValue>
			);
		},
		[condensed],
	);

	customComponents.ValueContainer = getValueContainer(condensed || false);
	customComponents.GroupHeading = GroupHeading;

	customComponents.Placeholder = React.useCallback(
		({ children, ...rest }: PlaceholderProps<UserSelectorOption, false>) => (
			<components.Placeholder {...rest}>
				{placeholderIcon && <FontAwesomeIcon className={styles.placeholderIcon} icon={placeholderIcon} />}
				{children}
			</components.Placeholder>
		),
		[placeholderIcon],
	);

	const debounceLoadOptions = React.useMemo(
		() =>
			debounce(
				loadOptions(
					{ excludeUserIDs, excludeGuests, ranked, doesNotReportUnder, excludeManagedUsers, excludeNonManagers },
					includedOptions,
					groupLabel,
				),
				300,
			),
		[
			excludeUserIDs,
			excludeGuests,
			ranked,
			doesNotReportUnder,
			excludeManagedUsers,
			excludeNonManagers,
			includedOptions,
			groupLabel,
		],
	);

	return (
		<AsyncSelector
			{...rest}
			formatGroupLabel={formatGroupLabels}
			isClearable={isClearable}
			components={customComponents}
			loadOptions={debounceLoadOptions}
			value={selectedUser}
		/>
	);
}

export type { SingleValueType, SelectorOption };
